import React, {useState, useEffect, useRef} from 'react';
import SearchResultCard from '../Components/SearchCard';
import { getBusinessCategories, getSalonsList } from '../Common/requests';
import { SALONS_LIST_SIZE } from '../Common/constants';
import { usePostHog } from '@posthog/react';
import { Link } from 'react-router-dom';
import { ReactSVG } from 'react-svg';
import SearchSVG from '../assets/icons/search.svg'
import CrossSVG from '../assets/icons/cross.svg'
import LocationSVG from '../assets/icons/location.svg'
import { useMediaQuery } from 'react-responsive';
import Slider from "react-slick";
import NotFound from '../assets/images/not-found.png';
import { useTranslation } from 'react-i18next';

const Search = () => {
    const { t } = useTranslation('marketplace');
    const posthog = usePostHog();
    const isTab = useMediaQuery({ query: '(max-width: 1024px)' });
    const [searching, setSearching] = useState(true);
    const [salons, setSalons] = useState([]);
    const [gender, setGender] = useState('');
    const [name, setName] = useState('');
    const [category, setCategory] = useState(0);
    const [placeText, setPlaceText] = useState('');
    const [coords, setCoords] = useState(null);
    const placeInputRef = useRef(null);

    const [dropdown, setDropdown] = useState(false);
    const [categories, setCategories] = useState([]);

    const showMenu = () => setDropdown(!dropdown);
    const hideMenu = () => setDropdown(false);

    const genderList = [
        { value: '', label: t('search.genderAll') },
        { value: 'both', label: t('search.genderUnisex') },
        { value: 'female', label: t('search.genderFemale') },
        { value: 'male', label: t('search.genderMale') }
    ];
    
    useEffect(() => {
        window.scroll(0, 0);
        
        async function fetchCategories() {
            const response = await getBusinessCategories();
            setCategories(Array.isArray(response) ? response : []);
        }
        
        fetchCategories();
    }, []);

    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 50; // ~10s at 200ms intervals

        const interval = setInterval(() => {
            attempts += 1;
            if (window.google?.maps?.places && placeInputRef.current) {
                clearInterval(interval);

                const autocomplete = new window.google.maps.places.Autocomplete(placeInputRef.current, { types: ['geocode'] });
                autocomplete.addListener('place_changed', () => {
                    const place = autocomplete.getPlace();
                    const lat = place.geometry?.location?.lat();
                    const lng = place.geometry?.location?.lng();
                    if (lat != null && lng != null) {
                        setCoords({ lat, lng });
                        setPlaceText(place.formatted_address || place.name || '');
                    }
                });
            } else if (attempts >= maxAttempts) {
                clearInterval(interval);
            }
        }, 200);

        return () => clearInterval(interval);
    }, []);

    const categoriesList = () => {
        return categories?.map((item)=>{
            return <Link key={item.id} to="#" className="label label-categories" onClick={()=>{
                setCategory(item.id);
                setName(item.name);
                hideMenu();
                posthog?.capture('search_category_selected', { category_id: item.id, category_name: item.name });
            }}>{item.name}</Link>
        });
    }

const settings = {
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 4,
    infinite: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        },
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true
        },
      },
    ],
  };

    const fetchSalonsList = async () => {
        setSearching(true);
        const data = {
            name: category === 0 ? name : "",
            gender: gender,
            category: category,
            page: 1,
            size: SALONS_LIST_SIZE,
            ...(coords ? { lat: coords.lat, lng: coords.lng } : {})
        };

        const response = await getSalonsList(data);
        setSearching(false);
        setSalons(response?.featuredSaloons || []);
    };

    const handleSearchButtonClick = () => {
        posthog?.capture('search_performed', {
            query: name,
            gender_filter: gender || 'all',
            category_id: category,
            has_location: !!coords,
        });
        fetchSalonsList();
    };

    useEffect(() => {
        // Only fetch if name is valid (empty or >= 3 chars)
        if (name.length >= 3 || name.length === 0) {
            fetchSalonsList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gender, category, name, coords]);

    const sortOrder = ['PREMIUM', 'PREMIUM_PLAN_YEARLY', 'PREMIUM_PLAN_MONTHLY', 'BASIC_PLAN_YEARLY', 'BASIC_PLAN_MONTHLY', 'FREE'];
    
    const sortedSalons = React.useMemo(() => {
        if (!salons || salons.length === 0) return [];
        
        const orderForIndexVals = sortOrder.slice().reverse();
        return [...salons].sort((a, b) => {
            const aIndex = -orderForIndexVals.indexOf(a.subscriptionType);
            const bIndex = -orderForIndexVals.indexOf(b.subscriptionType);
            return aIndex - bIndex;
        });
    }, [salons]);

    return (
        <>
            <div className="search-banner">
                <div className="container">
                    <div className="search-pill">
                        <div className={`search-pill-segment search-pill-treatment ${dropdown ? "active" : ""}`}>
                            <ReactSVG className="icon" src={SearchSVG} />
                            <input
                                type="text"
                                className="pill-input"
                                onFocus={showMenu}
                                onBlur={hideMenu}
                                placeholder={t('search.allTreatments')}
                                onChange={(e)=>{setName(e.target.value); setCategory(0)}}
                                value={name}
                            />
                            {name && (
                                <ReactSVG className="pill-clear" src={CrossSVG} onClick={()=>{setCategory(0); setName(''); setDropdown(false);}}/>
                            )}
                            <div className="search-dropdown" onMouseDown={(e) => e.preventDefault()}>
                                <div className="suggestions">
                                    <Link to="#" className="label label-categories" onClick={()=>{setCategory(0); setName(""); hideMenu();}}>{t('search.allCategories')}</Link>
                                    {categoriesList()}
                                </div>
                            </div>
                        </div>
                        <div className="search-pill-divider" />
                        <div className="search-pill-segment search-pill-location">
                            <ReactSVG className="icon" src={LocationSVG} />
                            <input
                                ref={placeInputRef}
                                type="text"
                                className="pill-input"
                                placeholder={t('search.currentLocation')}
                                onChange={(e) => { setPlaceText(e.target.value); if (!e.target.value) setCoords(null); }}
                                value={placeText}
                            />
                            {placeText && (
                                <ReactSVG className="pill-clear" src={CrossSVG} onClick={() => { setPlaceText(''); setCoords(null); }} />
                            )}
                        </div>
                        <button type="button" className="search-pill-button" onClick={handleSearchButtonClick}>
                            {t('common:search')}
                        </button>
                    </div>
                    <div className="search-results-header">
                        {sortedSalons.length > 0 ? (
                            <h2>
                                {t('search.resultsCount', { count: sortedSalons.length })}
                                {category > 0 && categories[category - 1] && ` ${t('search.for')} ${categories[category - 1].name}`}
                            </h2>
                        ) : (
                            <h2>
                                {category > 0 && categories[category - 1]
                                    ? t('search.noResultsFor', { category: categories[category - 1].name })
                                    : (searching ? t('search.searching') : t('search.noResultsFound'))}
                            </h2>
                        )}
                        <div className="gender-toggle" role="group" aria-label={t('search.gender')}>
                            {genderList.map((item) => (
                                <button
                                    key={item.value || 'all'}
                                    type="button"
                                    className={`gender-toggle-btn ${gender === item.value ? 'active' : ''}`}
                                    onClick={() => {
                                        setGender(item.value);
                                        posthog?.capture('search_gender_filter_changed', { gender: item.value || 'all' });
                                    }}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className="search-page">
                <section className="search-results">
                    <div className="container">
                        {searching ? null :
                        <>
                            {sortedSalons.length === 0 ? (
                                <div className="not-found">
                                    <img alt="not found" title="not found" src={NotFound} />
                                    {t('home.noSalonsFound')} <br />
                                    {t('home.checkAgainSoon')}
                                </div>
                            ) : (
                                <>
                                    {isTab ? (
                                        <Slider {...settings}>
                                            {sortedSalons.map((item) => (
                                                <div className="item" key={item.id || item.businessId}>
                                                    <SearchResultCard item={item} />
                                                </div>
                                            ))}
                                        </Slider>
                                    ) : (
                                        <div className="flex">
                                            <div className="results">
                                                {sortedSalons.map((item) => (
                                                    <div className="item" key={item.id || item.businessId}>
                                                        <SearchResultCard item={item} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                        }
                    </div>
                </section>
                <div className="show-on-mobile">
                    <div className={`overlay ${dropdown ? "active" : ""}`} />
                </div>
            </div>
        </>
    );
}

export default Search;